
import React from 'react';
import Navbar from '@/components/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const PrivacyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="container mx-auto px-4 py-12 max-w-3xl flex-grow">
        <h1 className="text-3xl font-bold mb-8 text-center">Privacy Policy</h1>
        
        <Card className="mb-8">
          <CardContent className="pt-6">
            <p className="mb-4">
              Last Updated: April 17, 2025
            </p>
            
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-2">Introduction</h2>
                <p>
                  Krix AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access our services.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">Information We Collect</h2>
                <p className="mb-2">We may collect information about you in various ways, including:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Personal Data: Name, email address, phone number, and billing information when you register for our services.</li>
                  <li>Usage Data: Information on how you use our services, including IP address, browser type, pages visited, and time spent.</li>
                  <li>Cookies and Tracking Technologies: We use cookies and similar tracking technologies to track activity on our services.</li>
                </ul>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">How We Use Your Information</h2>
                <p className="mb-2">We may use the information we collect for various purposes, including:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>To provide and maintain our services</li>
                  <li>To notify you about changes to our services</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information to improve our services</li>
                  <li>To monitor the usage of our services</li>
                  <li>To detect, prevent, and address technical issues</li>
                </ul>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">Disclosure of Your Information</h2>
                <p className="mb-2">We may share your information with:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Service Providers: Third-party companies that assist us in providing our services.</li>
                  <li>Business Partners: Trusted partners with whom we collaborate.</li>
                  <li>Legal Requirements: When required by law or to protect our rights.</li>
                </ul>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">Security of Your Information</h2>
                <p>
                  We use administrative, technical, and physical security measures to protect your personal information. However, no data transmission over the Internet or storage system can be guaranteed to be 100% secure.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">Your Data Protection Rights</h2>
                <p className="mb-2">Depending on your location, you may have certain rights regarding your personal information, including:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>The right to access your personal information</li>
                  <li>The right to rectify inaccurate personal information</li>
                  <li>The right to erase your personal information</li>
                  <li>The right to restrict processing of your personal information</li>
                  <li>The right to object to processing of your personal information</li>
                  <li>The right to data portability</li>
                </ul>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">Changes to This Privacy Policy</h2>
                <p>
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-semibold mb-2">Contact Us</h2>
                <p>
                  If you have any questions about this Privacy Policy, please contact us at <a href="mailto:privacy@krix.app" className="text-primary hover:underline">privacy@krix.app</a>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <footer className="py-8 px-4 border-t">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-xl font-bold gradient-text">Krix AI</p>
              <p className="text-sm text-muted-foreground">© 2025 Krix AI. All rights reserved.</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Contact
              </Link>
              <Link to="/refund" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Refund Policy
              </Link>
              <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
